import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MessageSquarePlus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/page-header';
import { Button, Skeleton, useToast } from '@/components/ui';
import { cn, formatRelative } from '@/lib/utils';
import { assistantApi } from './assistant-api';
import { lastConversation } from './assistant-launcher';
import { assistantKeys, ChatView } from './chat-view';

/** /assistant — היסטוריית השיחות והשיחה במסך מלא. `?c=<id>` = השיחה הפתוחה. */
export function AssistantPage({ conversationId }: { conversationId: string | null }) {
  const { t } = useTranslation('assistant');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const conversations = useQuery({
    queryKey: assistantKeys.conversations,
    queryFn: ({ signal }) => assistantApi.conversations(signal),
  });

  const open = (id: string | null) => {
    lastConversation.set(id);
    void navigate({ to: '/assistant', search: id ? { c: id } : {}, replace: true });
  };

  const remove = useMutation({
    mutationFn: (id: string) => assistantApi.remove(id),
    onSuccess: (_r, id) => {
      void qc.invalidateQueries({ queryKey: assistantKeys.conversations });
      if (id === conversationId) open(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const work = (conversations.data ?? []).filter((c) => c.mode === 'WORK');

  return (
    <div className="flex h-[calc(100dvh-7rem)] flex-col">
      <PageHeader title={t('page.title')} description={t('page.description')} />
      <div className="mt-4 flex min-h-0 flex-1 gap-4">
        <aside aria-label={t('page.history')} className="hidden w-64 shrink-0 flex-col md:flex">
          <Button variant="secondary" size="sm" onClick={() => open(null)}>
            <MessageSquarePlus aria-hidden />
            {t('launcher.newChat')}
          </Button>
          <h2 className="mt-4 mb-2 text-xs font-medium text-fg-muted">{t('page.history')}</h2>
          {conversations.isPending ? (
            <Skeleton className="h-24" />
          ) : work.length === 0 ? (
            <p className="text-xs text-fg-subtle">{t('page.noHistory')}</p>
          ) : (
            <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
              {work.map((c) => (
                <li key={c.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => open(c.id)}
                    aria-current={c.id === conversationId ? 'true' : undefined}
                    className={cn(
                      'min-w-0 flex-1 rounded-(--radius-md) px-2 py-1.5 text-start text-sm',
                      c.id === conversationId
                        ? 'bg-accent-subtle font-medium text-accent'
                        : 'text-fg hover:bg-surface-hover',
                    )}
                  >
                    <span className="block truncate">{c.title ?? t('page.untitled')}</span>
                    <span className="block text-2xs text-fg-subtle">
                      {formatRelative(c.updatedAt)}
                    </span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 opacity-60 group-hover:opacity-100"
                    aria-label={`${t('page.deleteChat')}: ${c.title ?? t('page.untitled')}`}
                    onClick={() => {
                      if (window.confirm(t('page.deleteConfirm'))) remove.mutate(c.id);
                    }}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </aside>
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-(--radius-lg) border border-border bg-surface">
          <div className="flex justify-end border-b border-border p-1 md:hidden">
            <Button variant="ghost" size="sm" onClick={() => open(null)}>
              <MessageSquarePlus aria-hidden />
              {t('launcher.newChat')}
            </Button>
          </div>
          <ChatView
            conversationId={conversationId}
            onConversation={(c) => open(c.id)}
            onMissing={() => open(null)}
          />
        </section>
      </div>
    </div>
  );
}
